/**
 * WMS Dashboard Context
 *
 * React Context for managing dashboard state, layouts, and widget configurations.
 * Provides persistence via localStorage and drag-and-drop functionality.
 *
 * @author BizClick WMS
 * @version 1.0.0
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { DashboardLayout, WidgetInstance, DashboardState, WidgetSettings } from './types';
import { getDefaultLayout, getWidgetById } from './widgetRegistry';
import { createLayoutFromTemplate } from './templates/dashboardTemplates';
import * as localStorageHelper from '../../../../utils/localStorageHelper';
import { takePendingPins } from '../claude-query/view/pendingPins';

/**
 * Storage key for dashboard layouts
 */
const DASHBOARD_STORAGE_KEY = 'bizclick_wms_dashboard_layouts';
const CURRENT_LAYOUT_KEY = 'bizclick_wms_current_layout';

/**
 * Context value interface
 */
interface DashboardContextValue {
  state: DashboardState;
  // Layout management
  saveLayout: (layout: DashboardLayout) => void;
  loadLayout: (layoutId: string) => void;
  createLayout: (name: string) => DashboardLayout;
  deleteLayout: (layoutId: string) => void;
  duplicateLayout: (layoutId: string, newName: string) => DashboardLayout | null;
  createFromTemplate: (templateId: string, layoutName?: string) => DashboardLayout | null;
  /** Endrer innstillinger på et dashboard (oppdatering, karusell) uten å bytte til det */
  updateLayoutSettings: (layoutId: string, settings: Partial<Pick<DashboardLayout, 'refreshInterval' | 'carousel'>>) => void;
  // Widget management
  addWidget: (widgetId: string, position?: { x: number; y: number }, initialConfig?: Record<string, unknown>) => void;
  removeWidget: (instanceId: string) => void;
  updateWidgetPosition: (instanceId: string, position: { x: number; y: number }) => void;
  updateWidgetSize: (instanceId: string, size: { width: number; height: number }) => void;
  updateWidgetSettings: (instanceId: string, settings: Partial<WidgetSettings>) => void;
  reorderWidgets: (widgets: WidgetInstance[]) => void;
  // Edit mode
  toggleEditMode: () => void;
  setSelectedWidget: (instanceId: string | null) => void;
  // Fullscreen
  toggleFullscreen: () => void;
  // Reset
  resetToDefault: () => void;
}

/**
 * Create context
 */
const DashboardContext = createContext<DashboardContextValue | undefined>(undefined);

/**
 * Provider props
 */
interface DashboardProviderProps {
  children: ReactNode;
}

/**
 * Generate unique ID
 */
const generateId = (): string => `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

/**
 * Dashboard Provider Component
 */
export const DashboardProvider: React.FC<DashboardProviderProps> = ({ children }) => {
  const [state, setState] = useState<DashboardState>(() => {
    // Load from localStorage on init
    const savedLayouts = localStorageHelper.getItem<DashboardLayout[]>(DASHBOARD_STORAGE_KEY);
    const currentLayoutId = localStorageHelper.getItem<string>(CURRENT_LAYOUT_KEY);

    const defaultLayout = getDefaultLayout();
    const layouts = savedLayouts || [defaultLayout];
    const currentLayout = layouts.find((l) => l.id === currentLayoutId) || layouts[0] || defaultLayout;

    return {
      currentLayout,
      availableLayouts: layouts,
      isEditMode: false,
      selectedWidgetId: null,
      isFullscreen: false,
    };
  });

  /**
   * Persist layouts to localStorage
   */
  const persistLayouts = useCallback((layouts: DashboardLayout[], currentId: string) => {
    localStorageHelper.setItem(DASHBOARD_STORAGE_KEY, layouts);
    localStorageHelper.setItem(CURRENT_LAYOUT_KEY, currentId);
  }, []);

  /**
   * Save current layout
   */
  const saveLayout = useCallback(
    (layout: DashboardLayout) => {
      setState((prev) => {
        const updatedLayout = { ...layout, updatedAt: new Date().toISOString() };
        const layoutIndex = prev.availableLayouts.findIndex((l) => l.id === layout.id);
        const newLayouts =
          layoutIndex >= 0
            ? prev.availableLayouts.map((l, i) => (i === layoutIndex ? updatedLayout : l))
            : [...prev.availableLayouts, updatedLayout];

        persistLayouts(newLayouts, updatedLayout.id);

        return {
          ...prev,
          currentLayout: updatedLayout,
          availableLayouts: newLayouts,
        };
      });
    },
    [persistLayouts]
  );

  /**
   * Load a layout by ID
   */
  const loadLayout = useCallback(
    (layoutId: string) => {
      setState((prev) => {
        const layout = prev.availableLayouts.find((l) => l.id === layoutId);
        if (!layout) return prev;

        persistLayouts(prev.availableLayouts, layoutId);

        return {
          ...prev,
          currentLayout: layout,
          isEditMode: false,
          selectedWidgetId: null,
        };
      });
    },
    [persistLayouts]
  );

  /**
   * Endrer innstillinger på et dashboard uten å bytte til det (karusellen setter visningstid
   * og rekkefølge for alle dashboardene fra ett sted).
   */
  const updateLayoutSettings = useCallback(
    (layoutId: string, settings: Partial<Pick<DashboardLayout, 'refreshInterval' | 'carousel'>>) => {
      setState((prev) => {
        const newLayouts = prev.availableLayouts.map((l) =>
          l.id === layoutId ? { ...l, ...settings, updatedAt: new Date().toISOString() } : l
        );
        const currentLayout = newLayouts.find((l) => l.id === prev.currentLayout.id) ?? prev.currentLayout;
        persistLayouts(newLayouts, currentLayout.id);
        return { ...prev, availableLayouts: newLayouts, currentLayout };
      });
    },
    [persistLayouts]
  );

  /**
   * Create a new layout
   */
  const createLayout = useCallback(
    (name: string): DashboardLayout => {
      const newLayout: DashboardLayout = {
        id: generateId(),
        name,
        widgets: [],
        gridColumns: 12,
        gridRows: 8,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isDefault: false,
      };

      setState((prev) => {
        const newLayouts = [...prev.availableLayouts, newLayout];
        persistLayouts(newLayouts, newLayout.id);

        return {
          ...prev,
          currentLayout: newLayout,
          availableLayouts: newLayouts,
          isEditMode: true,
        };
      });

      return newLayout;
    },
    [persistLayouts]
  );

  /**
   * Delete a layout
   */
  const deleteLayout = useCallback(
    (layoutId: string) => {
      setState((prev) => {
        // Can't delete if it's the only layout
        if (prev.availableLayouts.length <= 1) return prev;

        const newLayouts = prev.availableLayouts.filter((l) => l.id !== layoutId);
        const newCurrent =
          prev.currentLayout.id === layoutId ? newLayouts[0] : prev.currentLayout;

        persistLayouts(newLayouts, newCurrent.id);

        return {
          ...prev,
          currentLayout: newCurrent,
          availableLayouts: newLayouts,
        };
      });
    },
    [persistLayouts]
  );

  /**
   * Duplicate a layout
   */
  const duplicateLayout = useCallback(
    (layoutId: string, newName: string): DashboardLayout | null => {
      const source = state.availableLayouts.find((l) => l.id === layoutId);
      if (!source) return null;

      const duplicated: DashboardLayout = {
        ...source,
        id: generateId(),
        name: newName,
        widgets: source.widgets.map((w) => ({ ...w, instanceId: generateId() })),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isDefault: false,
      };

      setState((prev) => {
        const newLayouts = [...prev.availableLayouts, duplicated];
        persistLayouts(newLayouts, duplicated.id);

        return {
          ...prev,
          currentLayout: duplicated,
          availableLayouts: newLayouts,
        };
      });

      return duplicated;
    },
    [state.availableLayouts, persistLayouts]
  );

  /**
   * Create a layout from a pre-configured template
   */
  const createFromTemplate = useCallback(
    (templateId: string, layoutName?: string): DashboardLayout | null => {
      const result = createLayoutFromTemplate(templateId, layoutName);
      if (!result) return null;

      const newLayout: DashboardLayout = {
        ...result,
        isDefault: false,
      };

      setState((prev) => {
        const newLayouts = [...prev.availableLayouts, newLayout];
        persistLayouts(newLayouts, newLayout.id);

        return {
          ...prev,
          currentLayout: newLayout,
          availableLayouts: newLayouts,
          isEditMode: false,
        };
      });

      return newLayout;
    },
    [persistLayouts]
  );

  /**
   * Add a widget to current layout
   */
  const addWidget = useCallback(
    (widgetId: string, position?: { x: number; y: number }, initialConfig?: Record<string, unknown>) => {
      const widgetDef = getWidgetById(widgetId);
      if (!widgetDef) return;

      setState((prev) => {
        // Find first available position if not specified
        const pos = position || findAvailablePosition(prev.currentLayout, widgetDef.minWidth, widgetDef.minHeight);

        const newWidget: WidgetInstance = {
          instanceId: generateId(),
          widgetId,
          position: pos,
          size: { width: widgetDef.minWidth, height: widgetDef.minHeight },
          settings: {
            refreshInterval: widgetDef.defaultRefreshInterval,
            showHeader: true,
            ...(initialConfig ? { customConfig: initialConfig } : {}),
          },
        };

        const updatedLayout = {
          ...prev.currentLayout,
          widgets: [...prev.currentLayout.widgets, newWidget],
          updatedAt: new Date().toISOString(),
        };

        const newLayouts = prev.availableLayouts.map((l) =>
          l.id === updatedLayout.id ? updatedLayout : l
        );

        persistLayouts(newLayouts, updatedLayout.id);

        return {
          ...prev,
          currentLayout: updatedLayout,
          availableLayouts: newLayouts,
        };
      });
    },
    [persistLayouts]
  );

  /**
   * Remove a widget from current layout
   */
  const removeWidget = useCallback(
    (instanceId: string) => {
      setState((prev) => {
        const updatedLayout = {
          ...prev.currentLayout,
          widgets: prev.currentLayout.widgets.filter((w) => w.instanceId !== instanceId),
          updatedAt: new Date().toISOString(),
        };

        const newLayouts = prev.availableLayouts.map((l) =>
          l.id === updatedLayout.id ? updatedLayout : l
        );

        persistLayouts(newLayouts, updatedLayout.id);

        return {
          ...prev,
          currentLayout: updatedLayout,
          availableLayouts: newLayouts,
          selectedWidgetId: prev.selectedWidgetId === instanceId ? null : prev.selectedWidgetId,
        };
      });
    },
    [persistLayouts]
  );

  /**
   * Update widget position
   */
  const updateWidgetPosition = useCallback(
    (instanceId: string, position: { x: number; y: number }) => {
      setState((prev) => {
        const updatedLayout = {
          ...prev.currentLayout,
          widgets: prev.currentLayout.widgets.map((w) =>
            w.instanceId === instanceId ? { ...w, position } : w
          ),
          updatedAt: new Date().toISOString(),
        };

        const newLayouts = prev.availableLayouts.map((l) =>
          l.id === updatedLayout.id ? updatedLayout : l
        );

        persistLayouts(newLayouts, updatedLayout.id);

        return {
          ...prev,
          currentLayout: updatedLayout,
          availableLayouts: newLayouts,
        };
      });
    },
    [persistLayouts]
  );

  /**
   * Update widget size
   */
  const updateWidgetSize = useCallback(
    (instanceId: string, size: { width: number; height: number }) => {
      setState((prev) => {
        const updatedLayout = {
          ...prev.currentLayout,
          widgets: prev.currentLayout.widgets.map((w) =>
            w.instanceId === instanceId ? { ...w, size } : w
          ),
          updatedAt: new Date().toISOString(),
        };

        const newLayouts = prev.availableLayouts.map((l) =>
          l.id === updatedLayout.id ? updatedLayout : l
        );

        persistLayouts(newLayouts, updatedLayout.id);

        return {
          ...prev,
          currentLayout: updatedLayout,
          availableLayouts: newLayouts,
        };
      });
    },
    [persistLayouts]
  );

  /**
   * Update widget settings
   */
  const updateWidgetSettings = useCallback(
    (instanceId: string, settings: Partial<WidgetSettings>) => {
      setState((prev) => {
        const updatedLayout = {
          ...prev.currentLayout,
          widgets: prev.currentLayout.widgets.map((w) =>
            w.instanceId === instanceId ? { ...w, settings: { ...w.settings, ...settings } } : w
          ),
          updatedAt: new Date().toISOString(),
        };

        const newLayouts = prev.availableLayouts.map((l) =>
          l.id === updatedLayout.id ? updatedLayout : l
        );

        persistLayouts(newLayouts, updatedLayout.id);

        return {
          ...prev,
          currentLayout: updatedLayout,
          availableLayouts: newLayouts,
        };
      });
    },
    [persistLayouts]
  );

  /**
   * Reorder widgets (for drag-and-drop)
   */
  const reorderWidgets = useCallback(
    (widgets: WidgetInstance[]) => {
      setState((prev) => {
        const updatedLayout = {
          ...prev.currentLayout,
          widgets,
          updatedAt: new Date().toISOString(),
        };

        const newLayouts = prev.availableLayouts.map((l) =>
          l.id === updatedLayout.id ? updatedLayout : l
        );

        persistLayouts(newLayouts, updatedLayout.id);

        return {
          ...prev,
          currentLayout: updatedLayout,
          availableLayouts: newLayouts,
        };
      });
    },
    [persistLayouts]
  );

  /**
   * Toggle edit mode
   */
  const toggleEditMode = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isEditMode: !prev.isEditMode,
      selectedWidgetId: null,
    }));
  }, []);

  /**
   * Set selected widget
   */
  const setSelectedWidget = useCallback((instanceId: string | null) => {
    setState((prev) => ({
      ...prev,
      selectedWidgetId: instanceId,
    }));
  }, []);

  /**
   * Toggle fullscreen
   */
  const toggleFullscreen = useCallback(() => {
    setState((prev) => {
      const newFullscreen = !prev.isFullscreen;

      if (newFullscreen) {
        document.documentElement.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }

      return {
        ...prev,
        isFullscreen: newFullscreen,
        isEditMode: false,
      };
    });
  }, []);

  /**
   * Reset to default layout
   */
  const resetToDefault = useCallback(() => {
    const defaultLayout = getDefaultLayout();
    setState((prev) => {
      const newLayouts = prev.availableLayouts.map((l) =>
        l.isDefault ? defaultLayout : l
      );

      persistLayouts(newLayouts, defaultLayout.id);

      return {
        ...prev,
        currentLayout: defaultLayout,
        availableLayouts: newLayouts,
      };
    });
  }, [persistLayouts]);

  /**
   * Handle fullscreen change events
   */
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setState((prev) => ({ ...prev, isFullscreen: false }));
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // «Pin til dashboard» fra Spør Compass: pinner ligger i kø (sessionStorage) fordi dashboardet ofte ikke er
  // montert når brukeren pinner. Køen tømmes ved montering og når hendelsen kommer (ingen dobbel widget).
  useEffect(() => {
    const addPendingPins = () => {
      for (const pin of takePendingPins()) {
        addWidget('compass-report', undefined, {
          reportId: pin.reportId,
          displayMode: pin.displayMode || 'table',
        });
      }
    };

    addPendingPins();
    window.addEventListener('compass-pin-to-dashboard', addPendingPins);
    return () => window.removeEventListener('compass-pin-to-dashboard', addPendingPins);
  }, [addWidget]);

  const value: DashboardContextValue = {
    state,
    saveLayout,
    loadLayout,
    createLayout,
    deleteLayout,
    duplicateLayout,
    createFromTemplate,
    updateLayoutSettings,
    addWidget,
    removeWidget,
    updateWidgetPosition,
    updateWidgetSize,
    updateWidgetSettings,
    reorderWidgets,
    toggleEditMode,
    setSelectedWidget,
    toggleFullscreen,
    resetToDefault,
  };

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
};

/**
 * Custom hook to use dashboard context
 */
/** Som useDashboard, men gir undefined utenfor DashboardProvider (f.eks. forhåndsvisning av en widget). */
export const useOptionalDashboard = (): DashboardContextValue | undefined => useContext(DashboardContext);

export const useDashboard = (): DashboardContextValue => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

/**
 * Find first available position in the grid
 */
function findAvailablePosition(
  layout: DashboardLayout,
  width: number,
  height: number
): { x: number; y: number } {
  const { gridColumns, gridRows } = layout;
  const occupied = new Set<string>();

  // Mark occupied cells
  layout.widgets.forEach((widget) => {
    for (let x = widget.position.x; x < widget.position.x + widget.size.width; x++) {
      for (let y = widget.position.y; y < widget.position.y + widget.size.height; y++) {
        occupied.add(`${x},${y}`);
      }
    }
  });

  // Find first position that fits
  for (let y = 0; y <= gridRows - height; y++) {
    for (let x = 0; x <= gridColumns - width; x++) {
      let fits = true;
      for (let dx = 0; dx < width && fits; dx++) {
        for (let dy = 0; dy < height && fits; dy++) {
          if (occupied.has(`${x + dx},${y + dy}`)) {
            fits = false;
          }
        }
      }
      if (fits) {
        return { x, y };
      }
    }
  }

  // Default to origin if no space found
  return { x: 0, y: 0 };
}

export default DashboardContext;
